// components/landing/DataCollectionSection.tsx
'use client';

import React from 'react';
import { Database, Wifi, Cloud, TrendingUp, Download, RefreshCw } from 'lucide-react';

const dataSources = [
  { name: '기상청 API', status: 'active', dataPoints: '24,532', icon: Cloud, color: 'blue' },
  { name: '농촌진흥청 API', status: 'active', dataPoints: '18,924', icon: TrendingUp, color: 'green' },
  { name: 'IoT 센서', status: 'active', dataPoints: '156,847', icon: Wifi, color: 'purple' },
  { name: '공공데이터', status: 'syncing', dataPoints: '9,234', icon: Download, color: 'orange' },
];

export default function DataCollectionSection() {
  return (
    <section className="relative bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-gray-900 dark:to-blue-900/20 overflow-hidden py-24 lg:py-32">
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* 왼쪽: 설명 */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 dark:bg-blue-900/30 rounded-full text-blue-700 dark:text-blue-300 text-sm font-medium mb-6">
              <Database className="w-4 h-4 mr-2" />
              데이터 수집
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              다양한 소스에서
              <span className="block text-blue-600 dark:text-blue-400 mt-2">실시간 데이터 수집</span>
            </h2>

            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
              기상청, 농촌진흥청 등 공공 API와 IoT 센서 데이터를 실시간으로 수집하고 통합합니다. 농업에 필요한 모든 데이터를 한곳에서
              관리하세요.
            </p>

            {/* 통계 */}
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">15+</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">데이터 소스</div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">200K+</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">일일 데이터 포인트</div>
              </div>
            </div>

            {/* 기술 스택 */}
            <div className="flex flex-wrap gap-2">
              {['REST API', 'WebSocket', 'MQTT', 'GraphQL', 'PostgreSQL'].map((tech) => (
                <span
                  key={tech}
                  className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium"
                >
                  {tech}
                </span>
              ))}
            </div>
            <button
              onClick={() => window.open('http://dataplus.kr:3000', '_blank', 'noopener,noreferrer')}
              className="group mt-10 px-8 py-4  text-white   bg-blue-600  hover:bg-blue-700 font-bold rounded-lg shadow-xl hover:shadow-2xl transition-all duration-200 transform hover:-translate-y-1 flex items-center gap-2"
            >
              dataplus+ 바로가기
              {/* <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /> */}
            </button>
          </div>

          {/* 오른쪽: 데이터 소스 카드 */}
          <div className="relative">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">활성 데이터 소스</h3>
                <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />
              </div>

              <div className="space-y-3">
                {dataSources.map((source, index) => {
                  const Icon = source.icon;
                  const colors = {
                    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
                    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
                    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
                    orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
                  };

                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:shadow-md transition-all"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${colors[source.color as keyof typeof colors]}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 dark:text-white">{source.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{source.dataPoints} 포인트</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-2 h-2 rounded-full ${source.status === 'active' ? 'bg-green-500 animate-pulse' : 'bg-yellow-500'}`}
                        ></div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{source.status === 'active' ? '활성' : '동기화중'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
